import React from 'react';

// Utils
import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_SHORT_TEXT,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
} from '../../util/types';
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import SectionTopFacilities from '../../components/SectionKeyFacilities/SectionTopFacilities.jsx';
import SectionCleaningFacilities from '../../components/SectionCleaningFacilities/SectionCleaningFacilities.jsx';
import SectionPetType from '../../components/SectionPetType/SectionPetType.jsx';
import SectionPetSizes from '../../components/SectionPetSizes/SectionPetSizes.jsx';
import SectionOtherServices from '../../components/SectionOtherServices/SectionOtherServices.jsx';
import SectionSafetyFeatures from '../../components/SectionSafetyFeatures/SectionSafetyFeatures.jsx';
import SectionAccommodationType from '../../components/SectionAccommodationType/SectionAccommodationType.jsx';
import SectionPreferredContactMethod from '../../components/SectionPreferredContactMethod/SectionPreferredContactMethod.jsx';
import SectionPreferredCommunicationMethod from '../../components/SectionPreferredCommunicationMethod/SectionPreferredCommunicationMethod.jsx';
import SectionProfessionalCertifications from '../../components/SectionProfessionalCertifications/SectionProfessionalCertifications.jsx';
import SectionAddOns from '../../components/SectionAddOns/SectionAddOns.jsx';
import SectionTermsAndConditions from './SectionTermsAndConditions.js';

import css from './ListingPage.module.css';
import SectionVaccinations from '../../components/SectionVaccinations/SectionVaccinations.jsx';
import SectionTours from '../../components/SectionTours/SectionTours.jsx';
import SectionTextWithIcons from '../../components/SectionTextWithIcons/SectionTextWithIcons.jsx';
import SectionOutdoorFeatures from '../../components/SectionOutdoorFeatures/SectionOutdoorFeatures.jsx';
import SectionEnumMaybe from '../../components/SectionEnumMaybe/SectionEnumMaybe.js';
import SectionHighlights from '../../components/SectionHighlights/SectionHighlights.jsx';
import SectionSpecialNeeds from '../../components/SectionSpecialNeeds/SectionSpecialNeeds.jsx';

/**
 * Renders custom listing fields.
 * - SectionDetailsMaybe is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnumMaybe is used if schemaType is 'multi-enum'
 * - SectionTextMaybe is used if schemaType is 'text'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const desiredOrder = [
  'top_facilities',
  'top_facilities_grooming',
  'other_facilities',
  'highlights',
  'cleaning',
  'other_notes',
  'pet_type',
  'pet_type_groomed',
  'Dog_Size',
  'kennel_type',
  'other_services',
  'grooming_services',
  'special_services',
  'safety_boarding',
  'safety_daycare',
  'safety_grooming',
  'other_safety',
  'preferred_contact_method',
  'updates',
  'updates_by_grooming',
  'professional_certifications',
  'grooming_certifications',
  'other_certifications',
  'add_ons',
  'add_ons_grooming',
  'other_add_ons',
  'other_add_ons_grooming',
  'vaccination_boarding',
  'vaccination_daycare',
  'other_vaccines',
  'handle_special_needs',
  'balcony_lawn_open_area',
  'other_outdoor_features',
  'feeding_boarder',
  'feeding_daycare',
  'feeding_grooming',
  'tours',
  'facility_type',
  'facility_type_grooming',
  'insurance_provider',
  'drop_off_window',
  'pick_up_window',
  'start_time_grooming',
  'end_time_grooming',
  'days_grooming',
  'emergency_contact_name',
  'relationship',
  'emergency_phone_number',
  'alternate_email',
  'vaccination_policy',
  'injury_policy',
  'dist_radius_charge',
  'refund_policy',
  'restrictions',
  'late_pickup_policy',
  'havePets',
];

const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  // Fields are shown by default. Set showConfig.displayOnListingPage to false to explicitly hide a field.
  const displayableFieldConfigs = listingFieldConfigs.filter(
    fieldConfig => fieldConfig.showConfig?.displayOnListingPage !== false
  );

  const isFieldForSelectedCategories = fieldConfig =>
    isFieldForCategory(currentCategories, fieldConfig);

  let propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const hiddenKeys = [
    'top_facilities',
    'top_facilities_grooming',
    'other_facilities',
    'full_legal_name',
    'license_number',
    'license_number_for_daycare',
    'grooming_license_number',
    'issuing_authority',
    'license_expiry_date',
    'government_id_proof',
    'business_certificate',
    'agreement',
    'consent',
    'languages_spoken',
    'approval_to_operate',
    'business_type',
    'sku',
    'rented_property',
    'featured_on_social',
    'insurance_policy_number',
    'contact_country_code',
    'contact_number',
    'contact_email',
    'phone_country_code',
    'emergency_contact_name',
    'relationship',
    'emergency_phone_number',
    'alternate_email',
    'preferred_contact_method',
    'tours',
    'feeding_boarder',
    'feeding_daycare',
    'feeding_grooming',
    'facility_type',
    'facility_type_grooming',
    'safety_boarding',
    'safety_daycare',
    'safety_grooming',
    'other_safety',
    'balcony_lawn_open_area',
    'vaccination_policy',
    'injury_policy',
    'dist_radius_charge',
    'refund_policy',
    'restrictions',
    'other_add_ons',
    'other_add_ons_grooming',
    'breed_size',
    'government_id_proof',
    'professional_certifications',
    'grooming_certifications',
    'add_ons',
    'add_ons_grooming',
    'other_add_ons',
    'updates',
    'updates_by_grooming',
  ];
  const groupedTextKeys = [
    'vaccination_policy',
    'injury_policy',
    'dist_radius_charge',
    'refund_policy',
    'restrictions',
    'late_pickup_policy',
  ];
  const excludedTextKeys = [
    'drop_off_window',
    'pick_up_window',
    'facility_type',
    'facility_type_grooming',
    'other_certifications',

    // 'emergency_contact_name',
    // 'relationship',
    // 'emergency_phone_number',
    // 'alternate_email',
  ];
  // Filter out hidden/excluded fields
  propsForCustomFields = propsForCustomFields.filter(f => !hiddenKeys.includes(f.key));

  const kennelTypeValue = publicData?.kennel_type;
  if (kennelTypeValue) {
    if (!propsForCustomFields.some(f => f.key === 'kennel_type')) {
      propsForCustomFields.splice(desiredOrder.indexOf('kennel_type'), 0, {
        key: 'kennel_type',
        schemaType: SCHEMA_TYPE_ENUM,
        heading: 'Accommodation Type',
        selectedOptions: kennelTypeValue,
      });
    }
  }

  // Sort by desired order
  propsForCustomFields.sort((a, b) => {
    const aIndex = desiredOrder.indexOf(a.key);
    const bIndex = desiredOrder.indexOf(b.key);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  const groupedTermsFields = propsForCustomFields.filter(
    f => groupedTextKeys.includes(f.key) && f.schemaType === SCHEMA_TYPE_TEXT
  );

  const separatedTextFields = propsForCustomFields.filter(
    f => excludedTextKeys.includes(f.key) && f.schemaType === SCHEMA_TYPE_TEXT
  );

  const remainingFields = propsForCustomFields.filter(
    f => !(excludedTextKeys.includes(f.key) || groupedTextKeys.includes(f.key))
  );

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;

    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(currentCategories, config);

    const { isDetail, label } = showConfig;

    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }

    return filteredConfigs;
  };

  return (
    <>
      {remainingFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;

        if (schemaType === SCHEMA_TYPE_MULTI_ENUM) {
          switch (key) {
            case 'top_facilities':
            case 'top_facilities_grooming':
              return <SectionTopFacilities key={key} {...fieldProps} />;
            case 'highlights':
              return <SectionHighlights key={key} {...fieldProps} />;
            case 'cleaning':
              return <SectionCleaningFacilities key={key} {...fieldProps} />;
            case 'pet_type':
            case 'pet_type_groomed':
              return <SectionPetType key={key} {...fieldProps} />;
            case 'Dog_Size':
              return <SectionPetSizes key={key} {...fieldProps} />;
            case 'other_services':
            case 'grooming_services':
            case 'special_services':
              return <SectionOtherServices key={key} {...fieldProps} />;
            case 'safety_boarding':
            case 'safety_daycare':
            case 'safety_grooming':
              return <SectionSafetyFeatures key={key} {...fieldProps} />;
            // case 'kennel_type':
            //   return <SectionAccommodationType key={key} {...fieldProps} />;
            case 'preferred_contact_method':
              return <SectionPreferredContactMethod key={key} {...fieldProps} />;
            case 'updates':
            case 'updates_by_grooming':
              return <SectionPreferredCommunicationMethod key={key} {...fieldProps} />;
            case 'professional_certifications':
            case 'grooming_certifications':
              return <SectionProfessionalCertifications key={key} {...fieldProps} />;
            case 'add_ons':
            case 'add_ons_grooming':
              return <SectionAddOns key={key} {...fieldProps} />;
            case 'vaccination_boarding':
            case 'vaccination_daycare':
              return <SectionVaccinations key={key} {...fieldProps} />;
            case 'balcony_lawn_open_area':
              return <SectionOutdoorFeatures key={key} {...fieldProps} />;
            case 'tours':
            case 'days_grooming':
              return <SectionTours key={key} {...fieldProps} />;
            case 'handle_special_needs':
              return <SectionSpecialNeeds key={key} {...fieldProps} />;
            default:
              return <SectionMultiEnumMaybe key={key} {...fieldProps} />;
          }
        }

        if (schemaType === SCHEMA_TYPE_ENUM) {
          switch (key) {
            case 'kennel_type':
              return <SectionAccommodationType key={key} {...fieldProps} />;

            default:
              return null;
          }
        }
        if (schemaType === SCHEMA_TYPE_TEXT) {
          return <SectionTextMaybe key={key} {...fieldProps} />;
        }

        if (schemaType === SCHEMA_TYPE_YOUTUBE) {
          return <SectionYoutubeVideoMaybe key={key} {...fieldProps} />;
        }

        return null;
      })}
      <SectionEnumMaybe {...props} isFieldForCategory={isFieldForSelectedCategories} />
      {separatedTextFields.length > 0 && (
        <div className={css.separateTextFieldsContainer}>
          {separatedTextFields.map(({ key, ...fieldProps }) => (
            <SectionTextWithIcons key={key} fieldKey={key} {...fieldProps} />
          ))}
        </div>
      )}
      <SectionDetailsMaybe
        {...props}
        isFieldForCategory={isFieldForSelectedCategories}
        pickExtendedDataFields={pickExtendedDataFields}
      />
      {groupedTermsFields.length > 0 && (
        <SectionTermsAndConditions key="terms_group" fields={groupedTermsFields} />
      )}
    </>
  );
};

export default CustomListingFields;
